<script setup lang="ts">
import { usePage } from '@inertiajs/vue3';
import {
    ArrowDown,
    ArrowUpRight,
    Calculator,
    ChartNoAxesCombined,
    CreditCard,
    PiggyBank,
    Umbrella,
} from 'lucide-vue-next';

const page = usePage();
const money = (cents: number) =>
    new Intl.NumberFormat(page.props.app.lang === 'en' ? 'en-IE' : 'fr-FR', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
    }).format(cents / 100);
const tools = [
    { key: 'savings', icon: PiggyBank },
    { key: 'loan', icon: Calculator },
    { key: 'buffer', icon: Umbrella },
];
</script>

<template>
    <div>
        <section
            id="patrimoine"
            class="landing-chapter border-t"
            aria-labelledby="wealth-title"
        >
            <div>
                <p class="landing-eyebrow">
                    {{ $t('landing.wealth.eyebrow') }}
                </p>
                <h2 id="wealth-title" class="landing-heading">
                    {{ $t('landing.wealth.title') }}
                </h2>
                <p class="landing-description">
                    {{ $t('landing.wealth.description') }}
                </p>
                <ul class="mt-7 space-y-5">
                    <li
                        v-for="item in [
                            { key: 'assets', icon: ChartNoAxesCombined },
                            { key: 'loans', icon: CreditCard },
                        ]"
                        :key="item.key"
                        class="flex gap-3"
                    >
                        <component
                            :is="item.icon"
                            class="text-terracotta mt-0.5 size-5 shrink-0"
                            aria-hidden="true"
                        />
                        <div>
                            <p class="text-sm font-medium">
                                {{ $t(`landing.wealth.${item.key}`) }}
                            </p>
                            <p
                                class="text-text-soft mt-1 text-sm leading-relaxed"
                            >
                                {{ $t(`landing.wealth.${item.key}_detail`) }}
                            </p>
                        </div>
                    </li>
                </ul>
            </div>
            <figure
                class="landing-motion bg-ink text-canvas dark:border-border dark:bg-surface dark:text-ink rounded-2xl border border-transparent p-5 shadow-[var(--shadow-overlay)] sm:p-8"
                data-landing-motion
            >
                <figcaption class="text-canvas/75 dark:text-text-soft text-xs">
                    {{ $t('landing.wealth.example') }}
                </figcaption>
                <dl class="mt-7">
                    <div
                        v-for="line in [
                            { key: 'wallets', amount: 620000 },
                            { key: 'holdings', amount: 1800000 },
                            { key: 'outstanding', amount: -950000 },
                        ]"
                        :key="line.key"
                        class="border-canvas/20 dark:border-border flex flex-wrap items-baseline justify-between gap-3 border-b py-4"
                    >
                        <dt
                            class="text-canvas/80 dark:text-text-soft max-w-[13rem] text-sm"
                        >
                            {{ $t(`landing.wealth.${line.key}`) }}
                        </dt>
                        <dd class="font-geist-mono text-lg tabular-nums">
                            {{ money(line.amount) }}
                        </dd>
                    </div>
                    <div class="pt-7">
                        <dt class="text-canvas/80 dark:text-text-soft text-sm">
                            {{ $t('landing.wealth.net') }}
                        </dt>
                        <dd
                            class="font-geist-mono mt-2 text-[clamp(2rem,4vw,3.2rem)] leading-tight tracking-tight tabular-nums"
                        >
                            {{ money(1470000) }}
                        </dd>
                    </div>
                </dl>
                <p class="text-canvas/75 dark:text-text-soft mt-3 text-xs">
                    {{ $t('landing.wealth.formula') }}
                </p>
                <p
                    class="text-canvas/75 border-canvas/20 dark:border-border dark:text-text-soft mt-7 border-t pt-5 text-xs leading-relaxed"
                >
                    {{ $t('landing.wealth.caption') }}
                </p>
            </figure>
        </section>

        <section
            id="simulations"
            class="py-12 sm:py-16 lg:py-20"
            aria-labelledby="planning-title"
        >
            <div
                class="grid items-end gap-5 lg:grid-cols-[1.2fr_1fr] lg:gap-20"
            >
                <div>
                    <p class="landing-eyebrow">
                        {{ $t('landing.planning.eyebrow') }}
                    </p>
                    <h2 id="planning-title" class="landing-heading mb-0">
                        {{ $t('landing.planning.title') }}
                    </h2>
                </div>
                <p class="landing-description lg:pb-1">
                    {{ $t('landing.planning.description') }}
                </p>
            </div>
            <div class="mt-9 grid gap-4 lg:grid-cols-3">
                <article
                    v-for="(tool, index) in tools"
                    :key="tool.key"
                    class="landing-motion group border-border bg-surface flex flex-col rounded-xl border p-5 sm:p-7"
                    data-landing-motion
                    :style="{ '--landing-delay': `${index * 80}ms` }"
                >
                    <div class="mb-7 flex items-center justify-between">
                        <span
                            class="bg-terracotta-soft text-terracotta inline-flex size-12 items-center justify-center rounded-full motion-safe:transition-transform motion-safe:duration-200 motion-safe:group-hover:-rotate-6"
                            ><component
                                :is="tool.icon"
                                class="size-6"
                                aria-hidden="true"
                        /></span>
                        <span
                            class="font-geist-mono text-text-soft text-xs"
                            aria-hidden="true"
                            >0{{ index + 1 }}</span
                        >
                    </div>
                    <h3 class="text-sm font-semibold">
                        {{ $t(`landing.planning.${tool.key}.title`) }}
                    </h3>
                    <p
                        class="font-display mt-3 text-2xl leading-snug tracking-tight"
                    >
                        {{ $t(`landing.planning.${tool.key}.question`) }}
                    </p>
                    <p
                        class="text-text-soft mt-4 flex-1 text-sm leading-relaxed"
                    >
                        {{ $t(`landing.planning.${tool.key}.description`) }}
                    </p>
                    <div class="border-border mt-7 border-t pt-5 text-xs">
                        <p class="text-text-soft">
                            {{ $t(`landing.planning.${tool.key}.input`) }}
                        </p>
                        <ArrowDown
                            class="text-terracotta my-3 size-4"
                            aria-hidden="true"
                        />
                        <p class="font-medium">
                            {{ $t(`landing.planning.${tool.key}.output`) }}
                        </p>
                    </div>
                </article>
            </div>
            <p class="text-text-soft mt-5 text-xs leading-relaxed">
                {{ $t('landing.planning.note') }}
            </p>
        </section>

        <section
            id="premiers-pas"
            class="border-border border-y py-12 sm:py-16"
            aria-labelledby="start-title"
        >
            <div class="max-w-2xl">
                <p class="landing-eyebrow">{{ $t('landing.start.eyebrow') }}</p>
                <h2 id="start-title" class="landing-heading">
                    {{ $t('landing.start.title') }}
                </h2>
                <p class="landing-description">
                    {{ $t('landing.start.description') }}
                </p>
            </div>
            <ol class="mt-10 grid gap-7 lg:grid-cols-3 lg:gap-10">
                <li
                    v-for="(step, index) in [
                        'account',
                        'transactions',
                        'budget',
                    ]"
                    :key="step"
                    class="landing-motion"
                    data-landing-motion
                    :style="{ '--landing-delay': `${index * 80}ms` }"
                >
                    <div
                        class="mb-5 flex items-center gap-5"
                        aria-hidden="true"
                    >
                        <span class="font-display text-terracotta text-5xl"
                            >0{{ index + 1 }}</span
                        ><span class="bg-border h-px flex-1" /><ArrowUpRight
                            class="text-text-soft size-4"
                        />
                    </div>
                    <h3 class="text-base font-medium">
                        {{ $t(`landing.start.${step}.title`) }}
                    </h3>
                    <p class="text-text-soft mt-2 text-sm leading-relaxed">
                        {{ $t(`landing.start.${step}.description`) }}
                    </p>
                </li>
            </ol>
        </section>
    </div>
</template>
